import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import "@/access.css";

// Keep the requested route mounted during sign-in: no lost supplier tab and no
// access to internal components before the authenticated role is known.
export default function AccessGate({ audience = "client", children }) {
  const { user, login, register, logout } = useAuth();
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const team = audience === "admin";

  if (user === null) return <main id="main-content" className="ep-access" role="status">Vérification de votre accès…</main>;
  if (user && (!team || user.role === "admin")) return children;

  const submit = async (event) => {
    event.preventDefault();
    // Read the actual submitted fields, including browser/password-manager fill.
    const fields = new FormData(event.currentTarget);
    const email = String(fields.get("email") || "").trim();
    const password = String(fields.get("password") || "");
    setBusy(true); setError("");
    try {
      if (!team && registering) {
        await register({ email, password, name: String(fields.get("name") || "").trim(), phone: "" });
      } else {
        await login(email, password);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(err?.response?.status === 401
        ? "Adresse e-mail ou mot de passe incorrect. Vérifiez aussi les informations proposées par votre gestionnaire de mots de passe."
        : typeof detail === "string" ? detail : "Connexion impossible pour le moment. Réessayez.");
    } finally { setBusy(false); }
  };

  return <main id="main-content" className="ep-access" data-testid={`${audience}-access`}>
    <section className="ep-access-intro">
      <span className="ep-eyebrow">EASY PIÈCES · {team ? "ÉQUIPE" : "CLIENTS"}</span>
      <h1>{team ? "Accès administrateur" : "Votre espace client"}</h1>
      <p>{team ? "Suivez les demandes, consultez les fournisseurs et préparez les devis. Cet espace est réservé aux comptes autorisés de l’équipe."
        : "Retrouvez les devis et commandes rattachés à votre compte. Vous pouvez aussi demander une pièce sans créer de compte."}</p>
      <Link className="ep-access-switch" to={team ? "/compte" : "/admin/demandes"}>
        {team ? "Vous êtes client ? Accéder à l’espace client →" : "Vous faites partie de l’équipe ? Accès administrateur →"}
      </Link>
      <Link to="/#demande-pl-tp" className="ep-access-switch">Demander une pièce sans compte →</Link>
      <Link to="/#recherche-pieces" className="ep-access-switch">Rechercher par immatriculation sans compte →</Link>
    </section>
    <section className="ep-access-card" aria-label={team ? "Connexion administrateur" : "Connexion client"}>
      {user ? <>
        <h2>Accès réservé à l’équipe</h2>
        <p role="alert">Votre compte client ne permet pas d’accéder à l’administration ni aux données fournisseurs.</p>
        <Link to="/compte" className="ep-button">Aller à mon espace client</Link>
        <button className="ep-access-switch" onClick={logout}>Se déconnecter pour utiliser un compte administrateur</button>
      </> : <>
        <h2>{!team && registering ? "Créer mon compte client" : team ? "Connexion administrateur" : "Connexion client"}</h2>
        <form key={`${audience}-${registering}`} onSubmit={submit} aria-label={team ? "Connexion administrateur" : "Accès client"}>
          {!team && registering && <label>Nom complet<input name="name" autoComplete="name" required minLength={2} /></label>}
          <label>Adresse e-mail<input name="email" type="email" autoComplete="username" required /></label>
          <label>Mot de passe<input name="password" type="password" autoComplete={!team && registering ? "new-password" : "current-password"} required minLength={6} /></label>
          {error && <p className="ep-access-error" role="alert">{error}</p>}
          <button className="ep-button" type="submit" disabled={busy}>{busy ? "Connexion…" : !team && registering ? "Créer mon compte client" : team ? "Entrer dans l’administration" : "Entrer dans mon espace client"}</button>
        </form>
        {!team && <button type="button" className="ep-access-switch" disabled={busy} onClick={() => { setRegistering(!registering); setError(""); }}>
          {registering ? "J’ai déjà un compte client" : "Nouveau client ? Créer mon compte"}
        </button>}
        <details className="ep-access-help"><summary>Mot de passe oublié ?</summary>
          <p>Pour récupérer votre accès, contactez Easy Pièces au <a href="tel:+262639628588">06 39 62 85 88</a>. La réinitialisation automatique par e-mail n’est pas encore disponible. Ne communiquez jamais votre mot de passe.</p>
        </details>
      </>}
    </section>
  </main>;
}
